import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import { AccordionButton } from '@/components/Button/AccordionButton';
import ActionButton from '@/components/Button/ActionButton';
import type { Software } from '@/components/Combobox/SoftwareSelectorCombobox';
import Icon from '@/components/Icon/Icon';
import { Spacer, VStack } from '@/components/Layout';
import { SoftwareSelect } from '@/components/Select/SoftwareSelect';
import StepperHeaderComponent from '@/components/StepperHeader';
import { Text } from '@/components/Typography/Text';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { ManualFormHash } from './-constants/constant';

export const Route = createFileRoute('/_staff/licenses/add/')({
  component: Introduce,
});

type AllocationType = 'ON_PREMISES';

type CommonProps = {
  selectedAllocationType: AllocationType | null;
  selectedSoftware: Software | null;
  setSelectedSoftware: Dispatch<SetStateAction<Software | null>>;
};

function Introduce() {
  const [selectedAllocationType] = useState<AllocationType | null>(
    'ON_PREMISES',
  );
  const [selectedSoftware, setSelectedSoftware] = useState<Software | null>(
    null,
  );

  return (
    <VStack className="mx-auto w-[1000px] ">
      <Header />
      <Spacer variant="xs" />
      <LicenseTypeRadioGroup
        selectedAllocationType={selectedAllocationType}
        selectedSoftware={selectedSoftware}
        setSelectedSoftware={setSelectedSoftware}
      />
      <Spacer variant="xxl" />
      <AdditionalOptions />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.add.intro.header',
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t('title')} />
    </VStack>
  );
}

function LicenseTypeRadioGroup({
  selectedAllocationType,
  selectedSoftware,
  setSelectedSoftware,
}: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.add.intro.body',
  });

  const navigate = useNavigate();

  const handleButtonClick = useCallback(() => {
    if (selectedAllocationType) {
      const baseNavigation = {
        to: '/licenses/add/$allocationType',
        params: { allocationType: selectedAllocationType },
        hash: ManualFormHash.GENERAL,
      } as const;

      // ソフトウェアが選択されている場合は softwareId を URL に載せる。
      // Software-ification 完了後は `selectedSoftware.id` が必ず存在する想定。
      if (selectedSoftware?.id) {
        navigate({
          ...baseNavigation,
          search: { softwareId: selectedSoftware.id },
        });
      } else {
        navigate(baseNavigation);
      }
    }
  }, [selectedAllocationType, selectedSoftware, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4"> {t('selectLicenseType')}</Text>
      <Spacer variant="l" />
      <SoftwareSelect
        value={selectedSoftware || undefined}
        onChange={(software) => setSelectedSoftware(software || null)}
      />
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t('fillOutDetails')}
      </ActionButton>
    </>
  );
}

function AdditionalOptions() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.add.intro.options',
  });

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge">{t('description')}</Text>
      <Spacer size={24} />
      <AccordionButton
        icon={
          <Icon
            iconName="DatabaseBackup"
            text={t('syncIT')}
            textColor="text-primary"
            colorVariant="primary"
          />
        }
        inDevelopment={true}
        className="w-full"
      />
      <Spacer size={24} />
      <AccordionButton
        icon={
          <Icon
            iconName="DatabaseBackup"
            text={t('bulkCsvUpload.title')}
            textColor="text-primary"
            colorVariant="primary"
          />
        }
        inDevelopment={true}
        className="w-full"
      />
    </VStack>
  );
}
